import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent } from '@angular/common/http';
import { Observable } from 'rxjs';
import { version as appVersion } from '../../../package.json';
import { AppSettings } from '../../config/config';

function getCookieValue(name: string): string | null {
  const cookie = document.cookie.split(';').find(c => c.trim().startsWith(name + '='));
  return cookie ? cookie.split('=')[1] : null;
}

/**
 * Attaches staff-app-specific headers to requests hitting the staff API
 * base URL, leaving the admin app's requests/interceptor untouched. Header
 * keys/casing mirror the mobile app's axios interceptor (api.js:22-49,
 * constant.js:183-196), with mobileos set to "web" (not a mobile OS name)
 * since these requests come from the web staff portal, not the app.
 */
@Injectable()
export class StaffApiInterceptor implements HttpInterceptor {
  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const isStaffRequest = req.url.startsWith(AppSettings.API_ENDPOINT_STAFF)
      || req.url.startsWith(AppSettings.API_ENDPOINT_STAFF_BASE);
    if (!isStaffRequest) {
      return next.handle(req);
    }

    // Parent-portal services (parent-diary/parent-attendance/parent-timetable)
    // hit these same base URLs but already set their own "authToken" header
    // from localStorage (the parent session's own token — see
    // ParentLoginComponent.onLoginSuccess()), unrelated to the "usertoken"
    // cookie this interceptor reads for staff/admin sessions. The backend
    // still requires the device/lang headers below on every request
    // regardless of who's calling (skipping this block entirely, as an
    // earlier version of this fix did, made parent requests fail with
    // "Please provide valid device") — only the auth header is
    // parent-request-specific, so only that one is conditional.
    const isParentRequest = req.headers.has('authToken');

    let headers = req.headers
      .set('lang', 'en')
      .set('appversion', appVersion)
      .set('mobileos', 'web')
      .set('osVersion', 'web')
      .set('Idempotency-Key', crypto.randomUUID());

    if (!isParentRequest) {
      const authToken = getCookieValue('usertoken');
      if (authToken) {
        headers = headers.set('authtoken', authToken);
      }
    }

    return next.handle(req.clone({ headers }));
  }
}
